import {
  describeModifierRule,
  formatMinor,
  minorToInput,
  moveInOrder,
  validateModifierRule,
} from '@didto/domain';
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Input,
  Loading,
  PageHeader,
  Sheet,
} from '@didto/ui';
import { useQuery } from '@tanstack/react-query';
import type { FormEvent } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '@/app/session';
import { useCommand } from '@/app/useCommand';
import type { ModifierGroup, ModifierOption } from '@/lib/catalog';
import { catalogKeys, fetchModifierGroups, fetchModifierOptions } from '@/lib/catalog';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { MoneyInput, moneyOrError } from './fields';
import './catalog.css';

/** Modifier groups (e.g. Milk, Sugar level, Add-ons) and their priced options. */
export function ModifierGroupsPage() {
  const s = useSession();
  const canManage = s.can('catalog.manage');
  // ADR-037: reordering is a command; none starts while the server cannot be reached
  const online = useOnline();
  const currency = s.me?.business?.currency ?? 'PHP';
  const cmd = useCommand([['catalog']]);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<ModifierGroup | 'new' | null>(null);
  const groups = useQuery({ queryKey: catalogKeys.groups, queryFn: fetchModifierGroups });
  const options = useQuery({ queryKey: catalogKeys.options, queryFn: fetchModifierOptions });
  const readError = groups.error ?? options.error;
  const visible = (groups.data ?? []).filter((g) => showArchived || !g.archived_at);
  const liveGroups = (groups.data ?? []).filter((x) => !x.archived_at);
  const optionsOf = (groupId: string) =>
    (options.data ?? []).filter((o) => o.modifier_group_id === groupId);
  const move = async (g: ModifierGroup, dir: -1 | 1) => {
    const ids = moveInOrder(
      liveGroups.map((x) => x.id),
      liveGroups.findIndex((x) => x.id === g.id),
      dir,
    );
    if (!ids) return;
    await cmd.mutateAsync({ kind: 'modifier_group.reorder', payload: { ordered_ids: ids } });
  };
  const current =
    editing && editing !== 'new'
      ? ((groups.data ?? []).find((g) => g.id === editing.id) ?? editing)
      : editing;

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Catalog"
        title="Modifier groups"
        subtitle="Choices a cashier makes per item: milk, sugar level, add-ons. Assign groups to products from the product sheet."
        actions={
          <>
            <Link to="/catalog" className="d-btn d-btn--ghost">
              Products
            </Link>
            {canManage && (
              <Button variant="primary" onClick={() => setEditing('new')} data-testid="new-group">
                New group
              </Button>
            )}
          </>
        }
      />
      <div className="d-spread">
        <Checkbox checked={showArchived} onChange={setShowArchived} label="Show archived" />
      </div>
      {readError && (
        <ErrorState
          title="Could not load the modifier groups"
          message={errorText(readError)}
          onRetry={() => {
            if (groups.isError) void groups.refetch();
            if (options.isError) void options.refetch();
          }}
        />
      )}
      <div className="d-list" data-testid="group-list">
        {groups.isLoading && <Loading rows={3} label="Loading modifier groups…" />}
        {visible.map((g) => {
          const live = optionsOf(g.id).filter((o) => !o.archived_at);
          const at = liveGroups.findIndex((x) => x.id === g.id);
          return (
            <div key={g.id} className="d-row" data-testid={`group-row-${g.id}`}>
              <div className="d-row__main">
                <div className="d-row__title">
                  {g.name} {g.archived_at && <Badge>archived</Badge>}{' '}
                  <Badge tone={g.min_select > 0 ? 'info' : 'neutral'}>
                    {describeModifierRule(g)}
                  </Badge>
                </div>
                <div className="d-row__meta">
                  {live.length === 0 ? 'No options yet' : live.map((o) => o.name).join(' · ')}
                </div>
              </div>
              <div className="d-actions">
                {canManage && !g.archived_at && (
                  <>
                    <IconButton
                      size="sm"
                      variant="quiet"
                      label={`move ${g.name} up`}
                      disabled={!online || at <= 0}
                      onClick={() => void move(g, -1)}
                    >
                      ↑
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="quiet"
                      label={`move ${g.name} down`}
                      disabled={!online || at < 0 || at >= liveGroups.length - 1}
                      onClick={() => void move(g, 1)}
                    >
                      ↓
                    </IconButton>
                  </>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(g)}
                  aria-label={`open ${g.name}`}
                >
                  {canManage && !g.archived_at ? 'Edit' : 'Open'}
                </Button>
              </div>
            </div>
          );
        })}
        {groups.isSuccess && visible.length === 0 && <EmptyState title="No modifier groups yet" />}
      </div>
      {current && (
        <GroupSheet
          group={current === 'new' ? null : current}
          options={current === 'new' ? [] : optionsOf(current.id)}
          currency={currency}
          readOnly={!canManage}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function GroupSheet({
  group,
  options,
  currency,
  readOnly,
  onClose,
}: {
  group: ModifierGroup | null;
  options: ModifierOption[];
  currency: string;
  readOnly: boolean;
  onClose: () => void;
}) {
  // ADR-037: saving, archiving and restoring are commands; none starts without the server
  const online = useOnline();
  const cmd = useCommand([['catalog']]);
  const [name, setName] = useState(group?.name ?? '');
  const [min, setMin] = useState(String(group?.min_select ?? 0));
  const [max, setMax] = useState(
    group?.max_select === null || group?.max_select === undefined ? '' : String(group.max_select),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const archived = !!group?.archived_at;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const rule = { min_select: Number(min), max_select: max.trim() === '' ? null : Number(max) };
    const problem = validateModifierRule(rule);
    setError(problem);
    if (problem) return;
    setBusy(true);
    try {
      if (group)
        await cmd.mutateAsync({
          kind: 'modifier_group.update',
          payload: { modifier_group_id: group.id, name: name.trim(), ...rule },
        });
      else {
        await cmd.mutateAsync({
          kind: 'modifier_group.create',
          payload: { name: name.trim(), ...rule },
        });
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open onClose={onClose} title={group ? group.name : 'New modifier group'}>
      <div className="d-stack catalog__sheet">
        <form onSubmit={(e) => void submit(e)} className="d-stack">
          <Field label="Name" help="e.g. Milk, Sugar level, Add-ons">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={60}
                autoFocus={!group}
                disabled={readOnly || archived}
                data-testid="group-name"
              />
            )}
          </Field>
          <div className="catalog__pair">
            <Field label="Minimum choices" help="0 makes the group optional">
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min={0}
                  max={100}
                  inputMode="numeric"
                  value={min}
                  onChange={(e) => setMin(e.target.value)}
                  disabled={readOnly || archived}
                  className="d-input--num catalog__num"
                  data-testid="group-min"
                />
              )}
            </Field>
            <Field label="Maximum choices" help="Empty means no limit" error={error}>
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min={1}
                  max={100}
                  inputMode="numeric"
                  value={max}
                  onChange={(e) => setMax(e.target.value)}
                  disabled={readOnly || archived}
                  aria-invalid={error ? true : undefined}
                  className="d-input--num catalog__num"
                  data-testid="group-max"
                />
              )}
            </Field>
          </div>
          {!readOnly && !archived && (
            <div className="d-sheet__actions">
              <Button
                type="submit"
                variant="primary"
                busy={busy}
                disabled={!online}
                data-testid="save-group"
              >
                {group ? 'Save' : 'Create group'}
              </Button>
            </div>
          )}
        </form>
        {group && !archived && (
          <OptionsPanel group={group} options={options} currency={currency} readOnly={readOnly} />
        )}
        {group && !readOnly && (
          <section className="d-stack d-stack--tight">
            <hr className="d-divider" />
            <h3 className="d-eyebrow">{archived ? 'Archived' : 'Danger zone'}</h3>
            {archived ? (
              <div className="d-spread">
                <span className="d-muted">
                  Archived groups stay on products but are hidden from the POS.
                </span>
                <Button
                  variant="ghost"
                  disabled={!online}
                  onClick={() =>
                    void cmd
                      .mutateAsync({
                        kind: 'modifier_group.restore',
                        payload: { modifier_group_id: group.id },
                      })
                      .then(onClose)
                  }
                  data-testid="restore-group"
                >
                  Restore
                </Button>
              </div>
            ) : (
              <div className="d-spread">
                <span className="d-muted">Archiving keeps history and can be undone.</span>
                <Button
                  variant="danger"
                  disabled={!online}
                  onClick={() =>
                    void cmd
                      .mutateAsync({
                        kind: 'modifier_group.archive',
                        payload: { modifier_group_id: group.id },
                      })
                      .then(onClose)
                  }
                  data-testid="archive-group"
                >
                  Archive
                </Button>
              </div>
            )}
          </section>
        )}
      </div>
    </Sheet>
  );
}

function OptionsPanel({
  group,
  options,
  currency,
  readOnly,
}: {
  group: ModifierGroup;
  options: ModifierOption[];
  currency: string;
  readOnly: boolean;
}) {
  const online = useOnline();
  const cmd = useCommand([['catalog']]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const live = options.filter((o) => !o.archived_at);
  const archived = options.filter((o) => !!o.archived_at);
  const move = async (o: ModifierOption, dir: -1 | 1) => {
    const ids = moveInOrder(
      live.map((x) => x.id),
      live.findIndex((x) => x.id === o.id),
      dir,
    );
    if (!ids) return;
    await cmd.mutateAsync({
      kind: 'modifier_option.reorder',
      payload: { modifier_group_id: group.id, ordered_ids: ids },
    });
  };
  return (
    <section className="d-stack d-stack--tight" data-testid="options-panel">
      <h3 className="d-eyebrow">Options</h3>
      <div className="d-list">
        {live.map((o, i) => (
          <div key={o.id} className="d-row" data-testid={`option-row-${o.id}`}>
            {editingId === o.id ? (
              <OptionForm
                option={o}
                currency={currency}
                onCancel={() => setEditingId(null)}
                onSave={async (v) => {
                  await cmd.mutateAsync({
                    kind: 'modifier_option.update',
                    payload: {
                      modifier_option_id: o.id,
                      name: v.name,
                      price_delta_minor: v.price_delta_minor,
                    },
                  });
                  setEditingId(null);
                }}
              />
            ) : (
              <>
                <div className="d-row__main">
                  <div className="d-row__title">{o.name}</div>
                  <div className="d-row__meta">
                    {o.price_delta_minor === 0 ? (
                      'no extra charge'
                    ) : (
                      <span className="d-mono">
                        {`${o.price_delta_minor > 0 ? '+' : ''}${formatMinor(o.price_delta_minor, currency)}`}
                      </span>
                    )}
                  </div>
                </div>
                {!readOnly && (
                  <div className="d-actions">
                    <IconButton
                      size="sm"
                      variant="quiet"
                      label={`move ${o.name} up`}
                      disabled={!online || i === 0}
                      onClick={() => void move(o, -1)}
                    >
                      ↑
                    </IconButton>
                    <IconButton
                      size="sm"
                      variant="quiet"
                      label={`move ${o.name} down`}
                      disabled={!online || i === live.length - 1}
                      onClick={() => void move(o, 1)}
                    >
                      ↓
                    </IconButton>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingId(o.id)}
                      data-testid={`edit-option-${o.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={!online}
                      onClick={() =>
                        void cmd.mutateAsync({
                          kind: 'modifier_option.archive',
                          payload: { modifier_option_id: o.id },
                        })
                      }
                    >
                      Archive
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {archived.map((o) => (
          <div key={o.id} className="d-row" data-testid={`option-row-${o.id}`}>
            <div className="d-row__main">
              <div className="d-row__title">
                {o.name} <Badge>archived</Badge>
              </div>
            </div>
            {!readOnly && (
              <div className="d-actions">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={!online}
                  onClick={() =>
                    void cmd.mutateAsync({
                      kind: 'modifier_option.restore',
                      payload: { modifier_option_id: o.id },
                    })
                  }
                >
                  Restore
                </Button>
              </div>
            )}
          </div>
        ))}
        {live.length === 0 && archived.length === 0 && <p className="d-muted">No options yet.</p>}
      </div>
      {!readOnly && (
        <OptionForm
          key={live.length}
          option={null}
          currency={currency}
          onSave={async (v) => {
            await cmd.mutateAsync({
              kind: 'modifier_option.create',
              payload: {
                modifier_group_id: group.id,
                name: v.name,
                price_delta_minor: v.price_delta_minor,
              },
            });
          }}
        />
      )}
    </section>
  );
}

function OptionForm({
  option,
  currency,
  onSave,
  onCancel,
}: {
  option: ModifierOption | null;
  currency: string;
  onSave: (v: { name: string; price_delta_minor: number }) => Promise<void>;
  onCancel?: () => void;
}) {
  const online = useOnline();
  const [name, setName] = useState(option?.name ?? '');
  const [delta, setDelta] = useState(option ? minorToInput(option.price_delta_minor) : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const d = moneyOrError(delta, currency, { allowNegative: true });
    setError(d.error);
    if (d.error) return;
    setBusy(true);
    try {
      await onSave({ name: name.trim(), price_delta_minor: d.minor ?? 0 });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="catalog__option-form"
      data-testid={option ? 'option-edit-form' : 'option-add-form'}
    >
      <Field label={option ? 'Name' : 'New option'}>
        {(id) => (
          <Input
            id={id}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={60}
            placeholder="e.g. Oat milk"
            data-testid="option-name"
          />
        )}
      </Field>
      <Field label="Price change" error={error} help="Negative allowed">
        {(id) => (
          <MoneyInput
            id={id}
            value={delta}
            onChange={setDelta}
            currency={currency}
            allowNegative
            testId="option-delta"
          />
        )}
      </Field>
      <div className="d-actions">
        {onCancel && (
          <Button variant="quiet" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant={option ? 'primary' : 'ghost'}
          busy={busy}
          disabled={!online}
          data-testid={option ? 'save-option' : 'add-option'}
        >
          {option ? 'Save' : 'Add option'}
        </Button>
      </div>
    </form>
  );
}
